import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Banknote, FileCheck2, Check, Loader2, Wallet, Trash2 } from "lucide-react";

const CHEQUE = ["request_received", "request_accepted", "printing", "signed", "dispatched", "cleared"];
const BANK = ["request_received", "request_accepted", "processing", "transferred", "credited"];
const LABELS = {
  request_received: "Request Received", request_accepted: "Request Accepted",
  printing: "Printing", signed: "Signed", dispatched: "Dispatched", cleared: "Cleared",
  processing: "Processing", transferred: "Transferred", credited: "Credited",
};
const money = (n) => "\u20B9" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthLabel = (m) => { if (!m) return ""; const [y, mo] = m.split("-"); return new Date(y, mo - 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" }); };
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Timeline({ payout }) {
  const stages = payout.method === "cheque" ? CHEQUE : BANK;
  const idx = stages.indexOf(payout.status);
  return (
    <ol className="mt-3" data-testid={`timeline-${payout.payout_id}`}>
      {stages.map((s, i) => {
        const done = i < idx, current = i === idx;
        return (
          <li key={s} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${done ? "bg-emerald-500 text-white" : current ? "bg-gold text-navy ring-2 ring-gold/40" : "bg-slate-200 text-slate-400"}`}>
                {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {i < stages.length - 1 && <span className={`h-5 w-0.5 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />}
            </div>
            <span className={`pt-0.5 text-sm ${current ? "font-semibold text-navy" : done ? "text-slate-600" : "text-slate-400"}`}>{LABELS[s]}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function SalaryPayouts() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ["founder", "admin"].includes(user?.role);
  const [month, setMonth] = useState(thisMonth());
  const [genMethod, setGenMethod] = useState("bank");

  const { data: payouts = [], isLoading } = useQuery({
    queryKey: ["payouts", isAdmin ? month : "mine"],
    queryFn: async () => (await api.get("/payouts", { params: isAdmin ? { month } : {} })).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["payouts"] });

  const generate = useMutation({
    mutationFn: () => api.post("/payouts/generate", { month, method: genMethod }),
    onSuccess: (r) => { toast.success(`Generated ${r.data.created} payout(s)${r.data.skipped ? `, ${r.data.skipped} already existed` : ""}.`); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not generate."),
  });
  const patch = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/payouts/${id}`, body),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Update failed."),
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/payouts/${id}`),
    onSuccess: () => { toast.success("Payout removed."); invalidate(); },
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Salary Payouts" subtitle="Track the status of your salary cheque or bank transfer" />
        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : payouts.length === 0 ? (
          <SectionCard><p className="py-8 text-center text-sm text-slate-400" data-testid="no-payouts">No salary payouts yet. They'll appear here once processed.</p></SectionCard>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {payouts.map((p) => (
              <SectionCard key={p.payout_id} testId={`payout-${p.payout_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-heading text-lg font-bold text-navy">{monthLabel(p.month)}</div>
                    <div className="text-2xl font-extrabold text-gold-dark">{money(p.amount)}</div>
                  </div>
                  <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${p.method === "cheque" ? "bg-navy/10 text-navy" : "bg-emerald-50 text-emerald-700"}`}>
                    {p.method === "cheque" ? <FileCheck2 className="h-3.5 w-3.5" /> : <Banknote className="h-3.5 w-3.5" />}
                    {p.method === "cheque" ? "Cheque" : "Bank Transfer"}
                  </span>
                </div>
                <Timeline payout={p} />
                {p.method === "cheque" && p.cheque_no ? <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid={`ref-view-${p.payout_id}`}>Cheque No: <span className="font-semibold text-navy">{p.cheque_no}</span></div> : null}
                {p.method === "bank" && p.bank_ref ? <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid={`ref-view-${p.payout_id}`}>Bank Ref / UTR: <span className="font-semibold text-navy">{p.bank_ref}</span></div> : null}
              </SectionCard>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Salary Payouts" subtitle="Issue salary by cheque or bank transfer and update each stage">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payout-month" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <Select value={genMethod} onValueChange={setGenMethod}>
          <SelectTrigger className="w-40" data-testid="gen-method"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
        </Select>
        <Button onClick={() => generate.mutate()} disabled={generate.isPending} data-testid="generate-payouts-btn" className="bg-navy text-gold hover:bg-navy/90">
          {generate.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Wallet className="mr-1 h-4 w-4" />} Generate from Payslips
        </Button>
      </PageHeader>

      <SectionCard title={`Payouts \u2014 ${monthLabel(month)}`}>
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : payouts.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="no-payouts">No payouts for this month. Create payslips first, then click "Generate from Payslips".</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Employee</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Method</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Cheque No / UTR</th><th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => {
                  const stages = p.method === "cheque" ? CHEQUE : BANK;
                  return (
                    <tr key={p.payout_id} className="border-b border-slate-100" data-testid={`payout-row-${p.payout_id}`}>
                      <td className="py-2 pr-3 font-medium text-navy">{p.employee_name}<div className="text-[11px] text-slate-400">{p.employee_id}</div></td>
                      <td className="py-2 pr-3 font-semibold text-gold-dark">{money(p.amount)}</td>
                      <td className="py-2 pr-3">
                        <Select value={p.method} onValueChange={(v) => patch.mutate({ id: p.payout_id, body: { method: v } })}>
                          <SelectTrigger className="w-36" data-testid={`method-${p.payout_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 pr-3">
                        <Select value={p.status} onValueChange={(v) => patch.mutate({ id: p.payout_id, body: { status: v } })}>
                          <SelectTrigger className="w-44" data-testid={`status-${p.payout_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{stages.map((s) => <SelectItem key={s} value={s}>{LABELS[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 pr-3">
                        <Input defaultValue={p.method === "cheque" ? p.cheque_no : p.bank_ref} placeholder={p.method === "cheque" ? "Cheque No" : "UTR / Ref"} data-testid={`ref-${p.payout_id}`} className="w-36"
                          onBlur={(e) => {
                            const v = e.target.value;
                            const cur = p.method === "cheque" ? p.cheque_no : p.bank_ref;
                            if (v !== cur) patch.mutate({ id: p.payout_id, body: p.method === "cheque" ? { cheque_no: v } : { bank_ref: v } });
                          }} />
                      </td>
                      <td className="py-2"><button onClick={() => remove.mutate(p.payout_id)} data-testid={`del-${p.payout_id}`} title="Remove" className="rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
